import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Paper,
  Typography,
} from "@mui/material";
import { PageHeader, SubtitleM } from "../../components/Typography";
import useApiCall from "../../hooks/useApiCall";

export default function PromptVariableTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    variable_name: "",
    variable_type: "static",
    data_type: "string",
    category: "",
    description: "",
    is_active: true,
    value_schema: {},
    metadata: {},
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Fetch template data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const response = await makeRequest(`/v2/prompt_variable_templates/${id}`, {
        method: 'GET',
        authTokens: tokens,
      });
      setFormData(response.template);
    } catch (error) {
      console.error("Error fetching template:", error);
      setError("Failed to load template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.variable_name) {
      errors.variable_name = "Variable name is required";
    } else if (!validateVariableName(formData.variable_name)) {
      errors.variable_name =
        "Must start with lowercase letter and contain only lowercase letters, numbers, and underscores";
    }

    if (!formData.variable_type) {
      errors.variable_type = "Variable type is required";
    }

    if (!formData.data_type) {
      errors.data_type = "Data type is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode) {
        await makeRequest(`/v2/prompt_variable_templates/${id}`, {
          method: 'PUT',
          body: { template: formData },
          authTokens: tokens,
        });
      } else {
        await makeRequest('/v2/prompt_variable_templates', {
          method: 'POST',
          body: { template: formData },
          authTokens: tokens,
        });
      }
      navigate("/admin/prompt-variable-templates");
    } catch (error) {
      console.error("Error saving template:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.details?.variable_name?.[0] ||
          "Failed to save template. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/prompt-variable-templates");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack sx={{ width: "100%", padding: "10px" }} spacing={3}>
      <Stack
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <PageHeader>
          {isEditMode ? "Edit" : "Create"} Prompt Variable Template
        </PageHeader>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ padding: "24px" }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Variable Name */}
            <TextField
              label="Variable Name"
              value={formData.variable_name}
              onChange={(e) =>
                handleChange("variable_name", e.target.value.toLowerCase())
              }
              required
              fullWidth
              disabled={isEditMode} // Cannot change name after creation
              error={Boolean(validationErrors.variable_name)}
              helperText={
                validationErrors.variable_name ||
                "Must start with lowercase letter, contain only lowercase letters, numbers, and underscores"
              }
              placeholder="e.g., ai_personality_guidelines"
            />

            {/* Preview */}
            {formData.variable_name && (
              <Box
                sx={{
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Usage in prompts:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "monospace", fontWeight: "bold" }}
                >
                  {`{{${formData.variable_name}}}`}
                </Typography>
              </Box>
            )}

            {/* Variable Type */}
            <TextField
              select
              label="Variable Type"
              value={formData.variable_type}
              onChange={(e) => handleChange("variable_type", e.target.value)}
              required
              fullWidth
              error={Boolean(validationErrors.variable_type)}
              helperText={
                validationErrors.variable_type ||
                "When is this variable provided?"
              }
            >
              {getVariableTypes().map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Stack>
                    <Typography variant="body1">{type.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {type.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            {/* Data Type */}
            <TextField
              select
              label="Data Type"
              value={formData.data_type}
              onChange={(e) => handleChange("data_type", e.target.value)}
              required
              fullWidth
              error={Boolean(validationErrors.data_type)}
              helperText={
                validationErrors.data_type || "What type of data is stored?"
              }
            >
              {getDataTypes().map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Stack>
                    <Typography variant="body1">{type.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {type.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            {/* Category */}
            <TextField
              select
              label="Category"
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              fullWidth
              helperText="Helps organize variables in autocomplete"
            >
              <MenuItem value="">None</MenuItem>
              {getCategories().map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  <Stack>
                    <Typography variant="body1">{cat.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {cat.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Explain what this variable is for and how it should be used"
              placeholder="e.g., Guidelines for AI personality and tone throughout conversations"
            />

            {/* Active Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                />
              }
              label="Active (shown in autocomplete)"
            />

            {/* Example Usage */}
            <Paper
              elevation={0}
              sx={{ padding: "16px", backgroundColor: "#f9f9f9" }}
            >
              <SubtitleM style={{ marginBottom: "8px" }}>
                Example Usage:
              </SubtitleM>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#fff",
                  padding: "12px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              >
                {`You are a helpful AI assistant.

Guidelines: {{${formData.variable_name || "variable_name"}}}

Follow these rules when responding to users.`}
              </Typography>
            </Paper>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving && <CircularProgress size={20} />}
              >
                {saving
                  ? "Saving..."
                  : isEditMode
                  ? "Update Template"
                  : "Create Template"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}

// Helper functions
const getVariableTypes = () => [
  {
    value: "static",
    label: "Static",
    description: "Set once at template level, same for all conversations",
  },
  {
    value: "runtime",
    label: "Runtime",
    description: "Provided per conversation (from agent deployment, business unit, etc.)",
  },
  {
    value: "dynamic",
    label: "Dynamic",
    description: "Changes during conversation (context, user info, etc.)",
  },
];

const getDataTypes = () => [
  {
    value: "string",
    label: "String",
    description: "Plain text value",
  },
  {
    value: "number",
    label: "Number",
    description: "Numeric value",
  },
  {
    value: "boolean",
    label: "Boolean",
    description: "True/False value",
  },
  {
    value: "object",
    label: "Object",
    description: "JSON object with multiple fields",
  },
  {
    value: "array",
    label: "Array",
    description: "List of values",
  },
];

const getCategories = () => [
  {
    value: "personality",
    label: "Personality",
    description: "AI tone, style, behavior",
  },
  {
    value: "business_rules",
    label: "Business Rules",
    description: "Company policies, guidelines",
  },
  {
    value: "context",
    label: "Context",
    description: "User info, session data",
  },
  {
    value: "configuration",
    label: "Configuration",
    description: "System settings, preferences",
  },
];

const validateVariableName = (name) => {
  // Must start with lowercase letter
  // Can contain lowercase letters, numbers, underscores
  const regex = /^[a-z][a-z0-9_]*$/;
  return regex.test(name);
};
